import { z } from 'zod'
import type { LocalStorageKey } from '@/enums/local-storage-key'
import { isType } from '@/utils/functions'
import { joinText } from '@/utils/functions/join-text'
import { useEnv } from './use-env'

export function useStorage() {
  const { VITE_STORAGE_PREFIX_KEY } = useEnv()

  const getStorageData = <T extends z.ZodType>(
    option:
      | LocalStorageKey
      | {
          key: LocalStorageKey
          zodSchema: T
          autoRemove?: boolean
        }
  ): z.infer<T> | undefined => {
    const key = typeof option === 'string' ? option : option.key
    const zodSchema = typeof option === 'string' ? z.any() : option.zodSchema
    const autoRemove =
      typeof option === 'string' ? false : (option.autoRemove ?? false)

    const storageData = localStorage.getItem(getStorageKey(key))

    if (autoRemove) {
      removeStorageData(key)
    }

    if (!storageData) {
      return undefined
    }

    const data = parseStorageData(storageData)

    if (isType(data, zodSchema, true)) {
      return zodSchema.parse(data)
    } else {
      removeStorageData(key)
      return undefined
    }
  }

  const parseStorageData = (value: string): unknown => {
    try {
      const parseData = JSON.parse(value)

      return typeof parseData === 'object' ? parseData : value
    } catch {
      return value
    }
  }

  const setStorageData = (key: LocalStorageKey, value: any) => {
    localStorage.setItem(getStorageKey(key), value)
  }

  const removeStorageData = (key: LocalStorageKey) => {
    localStorage.removeItem(getStorageKey(key))
  }

  const getStorageKey = (key: string): string => {
    return `${joinText([VITE_STORAGE_PREFIX_KEY, key], '__')}__`
  }

  return {
    getStorageData,
    setStorageData,
    removeStorageData,
    getStorageKey
  }
}
